import { useLayoutEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PublicMarketingThemeControl, { usePublicMarketingTheme } from "@/components/PublicMarketingThemeControl";
import BrandLockup from "@/components/BrandLockup";

export default function LegalConsentPage() {
  const { user, loading, legalVersion, acceptLegal } = useAuth();
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [professional, setProfessional] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const { preference: publicThemePreference, resolvedTheme: publicResolvedTheme, setPreference: setPublicThemePreference } = usePublicMarketingTheme();
  const publicThemeClass = publicResolvedTheme === "dark"
    ? "public-fixed-dark dark marketing-theme-dark"
    : "marketing-theme-light";

  useLayoutEffect(() => {
    const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    scrollToTop();
    const frame = window.requestAnimationFrame(scrollToTop);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (loading) return <div className="legal-consent-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.legal_version === legalVersion) return <Navigate to="/dashboard" replace />;

  const accept = async (event) => {
    event.preventDefault();
    if (!privacy || !terms || !professional) return toast.error("All confirmations are required");
    setSaving(true);
    try {
      await acceptLegal({ privacy, terms, professional });
      toast.success("Preferences saved");
      nav("/dashboard", { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) ||
        "Could not save your acceptance"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`legal-consent-page public-marketing-theme ${publicThemeClass}`} data-marketing-theme={publicResolvedTheme}>
      <header className="legal-consent-header">
        <span className="legal-consent-brand">
          <BrandLockup className="brand-lockup--legal" />
        </span>
        <PublicMarketingThemeControl value={publicThemePreference} onChange={setPublicThemePreference} />
      </header>

      <main className="legal-consent-shell">
        <section className="legal-consent-card">
          <span className="legal-consent-card__accent" aria-hidden="true" />
          <div className="legal-consent-icon" aria-hidden="true">
            <ShieldCheck size={28} weight="duotone" />
          </div>
          <div className="brand-eyebrow">Private beta</div>
          <h1>Review the current notices</h1>
          <p className="legal-consent-description">
            Callsect&apos;s Privacy Notice has been updated to include short-lived transcript previews,
            explicit speaker-role confirmation, and direct transcript imports alongside Google Meet and audio.
            Review and confirm the current notices before continuing. Your acceptance time
            and document version will be recorded.
          </p>

          <div className="segmented-divider" />

          <form onSubmit={accept} className="legal-consent-form">
            <ConsentCheck checked={privacy} onChange={setPrivacy}>
              I have read and acknowledge the{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>.
            </ConsentCheck>
            <ConsentCheck checked={terms} onChange={setTerms}>
              I accept the{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">Private Beta Terms</Link>.
            </ConsentCheck>
            <ConsentCheck checked={professional} onChange={setProfessional}>
              I confirm that I am at least 18 and am using Callsect for professional evaluation.
            </ConsentCheck>

            <button
              type="submit"
              disabled={saving || !privacy || !terms || !professional}
              className="legal-consent-button"
            >
              <CheckCircle size={18} weight="fill" />
              {saving ? "Saving..." : "Accept and continue"}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}

function ConsentCheck({ checked, onChange, children }) {
  return (
    <label className={`legal-consent-check${checked ? " is-checked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="legal-consent-check__box" aria-hidden="true">
        {checked && <CheckCircle size={17} weight="fill" />}
      </span>
      <span>{children}</span>
    </label>
  );
}
